'use client';
import { ParsedUrlQuery } from 'querystring';

import React, {
  useRef,
  useState,
  useCallback,
  MutableRefObject,
  useEffect,
  useMemo,
} from 'react';

import { useMutation } from '@tanstack/react-query';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { Element } from 'react-scroll';
import { isArray } from 'lodash-es';
import dynamic from 'next/dynamic';
import { setCookie } from 'cookies-next';

import { useSearchBarContext } from '@contexts/hotel/SearchBar';
import GalleryHeroImages from '@components/galleries/GalleryHeroImages/GalleryHeroImages';
import SummaryCard from '@components/cards/summaries/hotel/SummaryCard';
import Breadcrumb from '@components/Breadcrumb/Breadcrumb';
import IsInViewPort from '@components/ui/IsInViewPort';
import { Separator, Layout } from '@components/formStyles';
import {
  BreadcrumbWrapper,
  ImagesWrapper,
  Wrapper,
  Container,
  BreadcrumbDesktop,
  HotelLocationWrapper,
  BasketErrorText,
  PaymentStartedErrorContainer,
  LinkText,
  SeoTextContainer,
} from '@components/views/hotel/detail/HotelDetailStyle';
import RoomOptions from '@components/views/hotel/detail/components/RoomOptions';
import SearchBar, {
  DetailSearchBox,
} from '@components/views/hotel/detail/components/search-bar/SearchBar';
import { formatArrayToString, getRoomInformations } from '@utils/utils';
import {
  LoyaltyType,
  RoomViewModel,
  ApiError,
  RatingProviderType,
} from '@api/v3';
import {
  cloudineryImage,
  getTypedFeatureProps,
} from '@views/hotel/detail/utils/features';
import { useAuthState } from '@contexts/auth/AuthContext';
import BankInstallments from '@components/ui/bank-installments/BankInstallments';
import { addHotelLetUsCallYouSuccessEventToGTM } from '@utils/gtm/hotel-gtm';
import useUnlockBasket from '@components/views/hooks/useUnlockBasket';
import {
  useBasketId,
  useIsKocAilemValidated,
  getBasketFromCache,
} from '@hooks/cache';
import { HotelDetailSchemaOrg } from '@utils/schemaorg/HotelDetailSchemaOrg';
import LoyaltyVerifications from '@components/ui/loyalty-verifications/LoyaltyVerifications';
import ScrollToTop from '@components/ScrollToTop/ScrollToTop';
import { callService } from '@api/remote-call';
import { ProviderType } from '@api/models/ProviderType';
import { Currencies } from '@constants/constants';
import { formatMomentDate, GTM_DATE_FORMAT } from '@utils/dateUtil';
import { useDevice } from '@contexts/device';
import { useOnce } from '@utils/gtm/hooks/useOnce';
import { addNoAvailability } from '@utils/gtm/events-gtm';
import { isClientSide } from '@utils/env';
import SeoText from '@components/views/landing/components/seoText/seoText';
import {
  useHideChatGPT,
  useSetChatGPTMessage,
} from '@components/ChatGPTLoader/hooks';
import { getDetailChatGPTMessage } from '@components/ChatGPTLoader/utils';
import { useOverrideLinkHref } from '@hooks/useOverrideLinkHref';
import { useToggle } from '@hooks/common';
import useBasketInformation, {
  useBasketMutation,
} from '@hooks/useBasketInformation';
import Typography from '@components/Typography';
import { ItemBoldText } from '@components/ui/PriceDetailsDrawer/PriceDetailsDrawerStyle';
import { hotelYouLike } from '@utils/newDataLayer/productYouLike';
import { hotelDetailAndAddtoCart } from '@utils/newDataLayer/productDetailAndAddtoCart';
import { useChatGPTContext } from '@contexts/chatGPT';
import { useInsiderBannerContext } from '@contexts/insiderBanners';
import { paramsToObject } from '@components/views/utils';
import useGetasPath from '@hooks/useGetasPath';
import { extractItems } from '@components/views/package/detail/helpers';
import CurrentCartPackagesModal from '@components/views/components/CurrentCartPackageModal';
import { useRemovePackage } from '@components/views/hooks/useRemovePackage';
import { generateUUID } from '@utils/generateUUID';
import { SessionScopeGTM } from '@utils/gtm/session-scope';

import { ModalView } from './components/details-galleries-modal';
import { PriceDetailsDrawerItem } from '../components/PriceDetails';
import {
  useHotelDetailState,
  useSetModalLoyaltyType,
  useSetSelectedVariations,
} from './context';
import { useDataRating } from './data-loader';
import { formatHotelImages } from './utils/formats';
import { RoomsVariationBarItem } from './components/rooms-variation-bar/Item/props';
import {
  getAvailableRoom,
  getLowestVariation,
  getOnlyAvailableRoomVariation,
  getRoomsGalleryCategory,
  groupImagesByCategory,
  sortRoomVariationItems,
} from './helpers';
import useCatchTrivagoUser from '../../../../integrations/trivago/useCatchTrivagoUser';
import HotelDetailEvents from './components/events';
import { useConceptHotelDetails } from '../hooks/useConceptHotelDetails';
import { useGetHotelRecommendations } from '../hooks/useGetHotelRecommendations';
import { useFetchHotelDetails } from '../hooks/useFetchHotelDetails';
import HotelBanners from './components/hotel-banners';
import { updateRoomParamInUrl } from '../listing/HotelListing';

const HotelDetailScoring = dynamic(
  () => import('./components/scoring/HotelDetailScoring')
);

const ExpediaHotelDetailScoring = dynamic(
  () => import('./components/expedia-scoring/HotelDetailScoring')
);

const ErrorInfoModal = dynamic(
  () => import('@components/views/components/ErrorInfoModal/ErrorInfoModal')
);

const RoomWarningInfo = dynamic(() => import('./components/room-warning'));

const HotelLocation = dynamic(
  () => import('./components/hotel-location/HotelLocation')
);
const HotelFeatures = dynamic(() => import('./components/hotel-features'));

const HotelRecommendations = dynamic(
  () => import('./components/hotel-recommendations')
);

const Announcements = dynamic(
  () => import('./components/announcements/Announcements')
);

const Map = dynamic(() => import('@components/ui/map/Map'), {
  ssr: false,
});

const Modal = dynamic(() => import('@components/Modal/next/Modal'), {
  ssr: false,
});

const ConfirmationModal = dynamic(
  () => import('@components/ui/modals/ConfirmationModal'),
  { ssr: false }
);

const HotelDetailConcept = dynamic(() => import('../concept'));

const GeneralLoader = dynamic(() => import('@components/ui/GeneralLoader'));
const AboutUs = dynamic(() => import('./components/about-us/AboutUs'));
const DetailsAndGalleries = dynamic(() =>
  import('./components/details-galleries-modal').then(
    (module) => module.default
  )
);
const PriceDetails = dynamic(() =>
  import('../components/PriceDetails').then((module) => module.default)
);
const Rules = dynamic(() => import('./components/hotelDetailRules/Rules'));
const HotelDetailSkeleton = dynamic(
  () => import('@components/views/hotel/detail/components/skeleton/Skeleton')
);

const ErrorModal = dynamic(() => import('@components/ui/modals/ErrorModal'), {
  ssr: false,
});

const ErrorPage = dynamic(() => import('@components/views/error/ErrorPage'), {
  ssr: false,
});

type ModalViewInfo = {
  galleriesDetailsModalShown: boolean;
  view: ModalView;
  entityType: 'hotel' | 'room';
};

const PAYMENT_STARTED = 'PAYMENT_STARTED';

type AddedRoomParams = {
  roomTypeId: string;
};
export interface HotelRooms extends RoomViewModel, AddedRoomParams {}
export type GalleryCategory = {
  url?: string;
  hotelImageCategoryName?: string;
  original: string;
  originalAlt: string;
  thumbnailAlt: string;
  thumbnail: string;
  description?: string;
};

const HotelDetail = ({
  initialData,
  conceptHotelInitialData,
}: {
  initialData: any;
  conceptHotelInitialData: any;
}) => {
  const { push } = useRouter();
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);
  const params = useParams();
  const asPath = useGetasPath();
  const { mutateAsync: removePackage, isLoading: isRemovePackageLoading } =
    useRemovePackage();

  const { userInfo } = useAuthState();
  const {
    setHotelDateSelection,
    startDate: inDate,
    endDate: outDate,
    hotelRoomsList: hotelRoomList,
  } = useSearchBarContext();
  const { basketInfo } = useBasketInformation();
  const { setPuanExtraBannerStatus } = useInsiderBannerContext();
  const [searchLoading, setSearchLoading] = useState(false);
  const setModalLoyaltyType = useSetModalLoyaltyType();
  const [hotelRooms, setHotelRooms] = useState<HotelRooms[]>([]);
  const { in: startDate, out: endDate, room = ['2'] } = query;
  const { isDesktop, isMobile } = useDevice();
  const { loyaltyType, selectedVariations, unSelectedRooms } =
    useHotelDetailState();
  const setSelectedVariations = useSetSelectedVariations();
  const roomsRef = useRef<HTMLElement>();
  const featuresRef = useRef<HTMLElement>();
  const aboutsRef = useRef<HTMLElement>();
  const announcementsRef = useRef<HTMLElement>();
  const scoringRef = useRef<HTMLElement>();
  const { setItemId, setItemType } = useChatGPTContext();

  const [showInstallments, setShowInstallments] = useState<boolean>(false);
  const [showErrorModalDeletePackage, setShowErrorModalDeletePackage] =
    useState(false);

  const [confirmPackageModal, toggleConfirmModal, setConfirmModal] =
    useToggle();

  const [errorModalStatusMessage, setErrorModalStatusMessage] = useState({
    title: '',
    description: '',
  });

  const ageValidation = useMemo(() => {
    if (
      errorModalStatusMessage.description?.startsWith(
        'ACCEPTABLE_MIN_AGE_VIOLATION'
      )
    ) {
      const age = errorModalStatusMessage.description?.slice(-2);
      return age?.startsWith('_') ? age.slice(1) : age;
    }
  }, [errorModalStatusMessage]);

  const isValidatedKocAilem = useIsKocAilemValidated();
  const trivagoUrl = updateRoomParamInUrl(asPath);
  useCatchTrivagoUser(trivagoUrl ?? '');
  const basketId = useBasketId();
  const [showMap, setShowMap] = useState(false);
  const [modalViewInfo, setGalleryDetailsModalViewInfo] =
    useState<ModalViewInfo>({
      galleriesDetailsModalShown: false,
      view: ModalView.NONE,
      entityType: 'hotel',
    });

  const [viewedRoom, setViewedRoom] = useState<RoomViewModel | undefined>(
    undefined
  );
  const [currentCartProductsModal, setCurrentCartProductsModal] =
    useState(false);
  const [isLetUsCallYouModalVisible, setIsLetUsCallYouModalVisible] =
    useState(false);
  const [hotelGalleries, setHotelGalleries] = useState<
    ReturnType<typeof groupImagesByCategory>
  >({});

  const showKocAilem = useMemo(() => {
    return isValidatedKocAilem ? false : query?.utm_source === 'kocailem';
  }, []);
  const [activeGalleryModalName, setActiveGalleryModalName] =
    useState<string>('');
  // ===============================
  // Handle view room details
  // ===============================
  const viewRoom = useCallback((room: RoomViewModel, view: ModalView) => {
    setActiveGalleryModalName('Oda Fotografları');
    setViewedRoom(room);
    setGalleryDetailsModalViewInfo({
      galleriesDetailsModalShown: true,
      view,
      entityType: 'room',
    });
  }, []);
  useEffect(() => {
    showKocAilem && setModalLoyaltyType(LoyaltyType.KOC_AILEM);
    return () => {
      setItemId(null);
      setItemType('');
    };
  }, []);

  // ===============================
  // Get the hotel detail data
  // ===============================
  const {
    data,
    refetch,
    isGetHotelDetailError,
    isLoading,
    isHotelFetching,
    isGetHotelDetailSuccess,
  } = useFetchHotelDetails({
    slug: params?.slug,
    startDate: formatArrayToString(startDate ?? ''),
    endDate: formatArrayToString(endDate ?? ''),
    query: query,
    room: room,
    initialData: initialData,
  });

  SessionScopeGTM(
    'Detay Sayfası',
    data?.isDomestic ? 'DP - Yurt İçi Otel' : 'DP - Yurt Dışı Otel'
  );
  useEffect(() => {
    setSearchLoading(false);
  }, [initialData]);

  // ===============================
  // Get the hotel detail concept  data
  // ===============================
  const { hotelDetailConceptData, isConceptLoading, isConceptError } =
    useConceptHotelDetails(
      params?.slug?.toString() ?? '',
      data?.isLocalHotel || false,
      conceptHotelInitialData
    );

  useHideChatGPT(Boolean(data?.isExpedia));
  useSetChatGPTMessage(getDetailChatGPTMessage(data?.name ?? ''));
  useOverrideLinkHref({
    elementId: 'insider-form-banner',
    queryParams: {
      name: data?.name,
      id: data?.id,
      countryCode: data?.countryCode,
    },
  });

  useEffect(() => {
    setHotelGalleries(
      groupImagesByCategory(
        data?.images ?? [],
        'hotelImageCategoryName',
        data?.name || 'Setur',
        data?.rooms
      )
    );
  }, [data?.images]);

  useOnce(() => {
    if (!data?.rooms || data?.rooms.length === 0) {
      addNoAvailability(data?.name, `${startDate} - ${endDate}`);
    }
  });

  useEffect(() => {
    if (data && Object.keys(data).length) {
      setItemId(data.id);
      setItemType('Hotel');
    }
  }, [data]);

  useEffect(() => {
    if (!data || !data?.rooms || !data?.rooms?.length) return;

    const roomsCategory: GalleryCategory[] = [];

    data?.rooms?.forEach((room) =>
      room.images.forEach((roomImages) =>
        roomsCategory.push({
          url: roomImages?.url ?? '/static/images/setur-static.jpg',
          hotelImageCategoryName: 'Odalar',
          original:
            cloudineryImage(roomImages?.url, '420', '250') ??
            '/static/images/setur-static.jpg',
          originalAlt: room?.name ?? 'Setur Görseli Hazırlanmakta',
          thumbnailAlt: room?.name ?? 'Setur Görseli Hazırlanmakta',
          thumbnail:
            cloudineryImage(roomImages?.url, '137', '65') ??
            '/static/images/setur-static.jpg',
          description:
            room.name && room.typeName ? room.name + ' ' + room.typeName : '',
        })
      )
    );

    setHotelGalleries((currentValue) => ({
      Odalar: roomsCategory,

      ...currentValue,
    }));
  }, [data]);

  useEffect(() => {
    if (!data || !data?.videos || !data?.videos?.length) return;

    const videoCategory: GalleryCategory[] = [];

    videoCategory.push({
      url: '',
      hotelImageCategoryName: 'Videolar',
      original: '',
      originalAlt: '',
      thumbnailAlt: '',
      thumbnail: '',
    });

    setHotelGalleries((currentValue) => ({
      Videolar: videoCategory,

      ...currentValue,
    }));
  }, [data]);

  useEffect(() => {
    return () => {
      setPuanExtraBannerStatus(false);
    };
  }, []);

  useEffect(() => {
    if (!data?.isExpedia) {
      setPuanExtraBannerStatus(true);
    } else {
      setPuanExtraBannerStatus(false);
    }
  }, [data]);

  const [viewedRoomGallery, setViewedRoomGallery] = useState<
    ReturnType<typeof groupImagesByCategory>
  >({});

  const { mutate: basketMutate, data: basketMutationData } =
    useBasketMutation();
  // ===============================
  // Get the hotel recommendations
  // ===============================
  const { hotelRecommendations } = useGetHotelRecommendations(
    data,
    formatArrayToString(startDate || ''),
    formatArrayToString(endDate || '')
  );
  const { ratingDetail } = useDataRating(data?.id ?? 0);
  const { unlockBasketAsync, cleanBasketSignal } = useUnlockBasket({
    basketId: basketId || '',
  });

  useEffect(() => {
    setViewedRoomGallery(
      groupImagesByCategory(viewedRoom?.images ?? [], '', 'Oda Fotoğrafı')
    );
  }, [viewedRoom?.images]);
  // ===============================
  // Handle add ot hotel to basket
  // ===============================
  const addBasket = async () => {
    const basketId =
      typeof window !== 'undefined' && localStorage.getItem('basketId');
    const body = selectedVariations?.map((room) => ({
      hotelId: data?.id || 0,
      roomId: room.roomId,
      in: formatArrayToString(startDate || ''),
      out: formatArrayToString(endDate || ''),
      cancellationPolicyId: room.cancelletionPolicyId,
      boardTypeId: room.boardTypeId,
      provider: room.provider,
      providerInfo: room.providerInfo
        ? room.providerInfo
        : (null as unknown as undefined),
      guest: {
        adultCount: room.guests.adultCount || 0,
        childBirthdates: room.guests.childrenBirthDates || [''],
      },
      guestSearch: {
        adultCount: data?.guests?.adultCount || 0,
        childBirthdates: data?.guests?.childrenBirthDates || [''],
      },
      extraServices: [],
      promotions: [room.promotionId],
      variationId: room.variationId,
      loyaltyType: room.loyaltyType || undefined,
      roomTypeId:
        room.provider === ProviderType.TATIL_BUDUR
          ? room.roomTypeId
          : (null as unknown as undefined),
      boardTypeName:
        room.provider === ProviderType.TATIL_BUDUR || data?.isExpedia
          ? room.boardTypeName
          : (null as unknown as undefined),
      roomName: room.roomName,
    }));

    hotelDetailAndAddtoCart(
      data,
      startDate,
      endDate,
      query,
      selectedVariations
    );

    await unlockBasketAsync();

    const basketState = await cleanBasketSignal;

    return await callService(
      { service: 'PrebookService', method: 'prebookAddHotel', version: 'v3' },
      {
        basketId: basketState === 'stale' || !basketId ? '' : basketId,
        body,
      }
    );
  };

  const prebookMutation = useMutation(['hotelPrebook', push], addBasket, {
    onSuccess: ({ basketId }) => {
      localStorage.setItem('basketId', basketId);
      setCookie('basketId', basketId);
      push(`/seyahat-planlama?bid=${basketId}`);
    },
    onError: (error: ApiError) => {
      if (error?.body?.errors?.[0]?.code === 'LOYALTY_SWITCH_ERROR') {
        const { basketId, errors } = error.body;
        sessionStorage.setItem(
          'switchError',
          JSON.stringify({
            message: errors?.[0]?.message || '',
            items: errors?.map((item) => item.errorItemId),
          })
        );
        localStorage.setItem('basketId', basketId);
        setCookie('basketId', basketId);
        setErrorModalStatusMessage({
          title: 'Uyarı',
          description: 'Loyalty promosyonu kazanmadan ürün eklenmektedir. ',
        });
        setTimeout(() => {
          push(`/seyahat-planlama?bid=${basketId}`);
        }, 1500);
      } else {
        setErrorModalStatusMessage({
          title: 'Ooops',
          description:
            error?.body?.errors?.[0]?.detail ||
            'Sepete ürün eklemeye çalışırken, genel bir hata oluştu, tekrar deneyiniz.',
        });
      }
    },
  });

  const handleConfirmRemoveCurrentCartPackage = async (): Promise<void> => {
    if (!basketMutationData) return;
    const basketItems = extractItems(basketMutationData);

    setCurrentCartProductsModal(false);

    await removePackage({
      basketId: basketMutationData?.id,
      itemId: basketItems?.[0]?.itemId,
    });

    prebookMutation.mutate();
  };

  // ===============================
  //Handle hotel drawer items
  // ===============================
  const drawerItems = useCallback(
    (): PriceDetailsDrawerItem[] =>
      selectedVariations?.map((variation) => {
        return {
          id: variation.variationId,
          name: variation.promotionInfoName,
          price: variation.totalDiscountedSellingPrice,
          tabNumber: variation.tabNumber,
        };
      }),
    [selectedVariations]
  );

  const handleRemoveVariation = (id: string): void =>
    setSelectedVariations(
      selectedVariations.filter((room) => room.variationId !== id)
    );

  const handleNavigateRooms = (): void => {
    if (data?.nightCount && !data?.rooms?.length) {
      roomsRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    } else {
      roomsRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };
  const ClickTripAdvisor = (): void => {
    scoringRef?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };
  const handleNavigateAnnouncment = (): void => {
    announcementsRef?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };
  const handleNavigateFeatures = (): void => {
    featuresRef?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  const handleNavigateAboutUs = (): void =>
    aboutsRef?.current?.scrollIntoView({ behavior: 'smooth' });

  const handleNavigateTravelPlanning = async (): Promise<void> => {
    const basketId =
      typeof window !== 'undefined' && localStorage.getItem('basketId');

    if (basketId) {
      basketMutate(basketId, {
        onSuccess: (data) => {
          if (data?.isPackageBasket) {
            setCurrentCartProductsModal(true);
          } else {
            setSearchLoading(true);
            if (basketInfo && basketInfo.isPackageBasket) {
              toggleConfirmModal();
            } else {
              prebookMutation.mutate();
            }
          }
        },
      });
    } else {
      setSearchLoading(true);
      if (basketInfo && basketInfo.isPackageBasket) {
        toggleConfirmModal();
      } else {
        prebookMutation.mutate();
      }
    }
  };

  const handleLoyaltySubmit = () => {
    refetch();
    setModalLoyaltyType(null);
  };

  const toggleMap = (): void =>
    data?.latitude && data.longitude
      ? setShowMap((state) => !state)
      : setShowMap(false);

  // ========================================
  // Toggle Installments Modal
  // ========================================
  const toggleInstallments = (): void => setShowInstallments((state) => !state);

  // ========================================
  // Toggle Features Gallery Modal
  // ========================================
  const [control, setControl] = useState(false);
  const toggleFeaturesModal = () => {
    setControl(false);
    setActiveGalleryModalName('all');
    setStartImage(0);
    setGalleryDetailsModalViewInfo({
      galleriesDetailsModalShown: true,
      view: ModalView.GALLERIES,
      entityType: 'hotel',
    });
  };

  const toggleVideoFeaturesModal = () => {
    setControl(true);
    setGalleryDetailsModalViewInfo({
      galleriesDetailsModalShown: true,
      view: ModalView.GALLERIES,
      entityType: 'hotel',
    });
  };
  const toggleActiveFeaturesModal = (activeGalleryTitle: string) => {
    setGalleryDetailsModalViewInfo({
      galleriesDetailsModalShown: true,
      view: ModalView.GALLERIES,
      entityType: 'hotel',
    });
    setTimeout(() => setActiveGalleryModalName(activeGalleryTitle));
  };

  const detailsAndGalleriesProps = {
    galleries:
      modalViewInfo.entityType === 'hotel' ? hotelGalleries : viewedRoomGallery,
    title:
      modalViewInfo.entityType === 'hotel'
        ? data?.name
        : viewedRoom?.typeName === ''
        ? `${viewedRoom?.name}`
        : `${viewedRoom?.name},${viewedRoom?.typeName}`,
    buttonActionMessage:
      modalViewInfo.entityType === 'hotel' ? 'Odaları Gör' : 'Oda Seç',
  };
  const scrollToBottom = () => {
    announcementsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  const featuresProps = getTypedFeatureProps({
    entityType: modalViewInfo.entityType,
    features:
      modalViewInfo.entityType === 'hotel'
        ? data?.features ?? []
        : viewedRoom?.features ?? [],
  });
  const errorInfoModalClosed = () => {
    setErrorModalStatusMessage({
      title: '',
      description: '',
    });
    prebookMutation.reset();
  };

  useEffect(() => {
    if (!data?.rooms || data?.rooms.length === 0) return;
    const rooms: HotelRooms[] = [];
    data?.rooms?.forEach((room) => {
      rooms.push({
        ...room,
        roomTypeId: generateUUID(),
      });
    });

    setHotelRooms(rooms);
  }, [data?.rooms]);

  useEffect(() => {
    if (isGetHotelDetailSuccess && !isHotelFetching) {
      setSearchLoading(false);
    }
  }, [isGetHotelDetailSuccess, isHotelFetching]);
  const totalImages = useMemo(() => {
    return (
      (getRoomsGalleryCategory(data?.rooms ?? [])?.length ?? 0) +
      (data?.images?.length ?? 0)
    );
  }, [data]);

  const RoomsVariationsItems = useMemo(() => {
    const roomsVariations: RoomsVariationBarItem[] = [];

    if (!hotelRooms || hotelRooms?.length === 0) return [];

    hotelRooms?.forEach((room) => {
      const lowestPriceItem = getLowestVariation(room.details);

      if (lowestPriceItem) {
        roomsVariations?.push({
          roomName:
            `${room?.name} ${room?.typeName ? ` - ${room?.typeName}` : ''} ` ||
            '',
          roomId: room?.roomTypeId ?? '',
          price: lowestPriceItem?.totalDiscountedSellingPrice || 0,
          currency: lowestPriceItem.currency || Currencies.TL,
          isActive: false,
          isAvailable: getAvailableRoom(room.details),
        });
      } else {
        roomsVariations?.push({
          roomName:
            `${room?.name} ${room?.typeName ? ` - ${room?.typeName}` : ''} ` ||
            '',
          roomId: room?.roomTypeId ?? '',
          price: 0,
          currency: '',
          isActive: false,
          isAvailable: false,
        });
      }
    });

    return [
      ...sortRoomVariationItems(
        getOnlyAvailableRoomVariation(roomsVariations, true)
      ),
      ...sortRoomVariationItems(
        getOnlyAvailableRoomVariation(roomsVariations, false)
      ),
    ];
  }, [hotelRooms, query]);

  useEffect(() => {
    if (
      showErrorModalDeletePackage ||
      confirmPackageModal ||
      prebookMutation.isError
    ) {
      setSearchLoading(false);
    }
  }, [
    showErrorModalDeletePackage,
    confirmPackageModal,
    prebookMutation.isError,
  ]);

  useEffect(() => {
    if (data?.id) hotelDetailAndAddtoCart(data, startDate, endDate, query, []);
  }, [data]);

  const [startImage, setStartImage] = useState(0);
  const [disable, setDisable] = useState(false);
  const pid = getBasketFromCache()?.pid;
  const showNormalHotel = useMemo(() => {
    if (data && conceptHotelInitialData) {
      return !!data?.isExpedia || Boolean(!data?.isLocalHotel);
    } else if (data && !conceptHotelInitialData) {
      return true;
    }
  }, [data, conceptHotelInitialData]);

  const showHotelSkeleton = useMemo(() => {
    return (
      isClientSide() && (isLoading || !!isConceptLoading || !!isHotelFetching)
    );
  }, [isLoading, isConceptLoading, isHotelFetching]);

  if (isGetHotelDetailError) return <ErrorPage />;
  const onCloseCallMeModal = () => {
    setIsLetUsCallYouModalVisible(false);
  };
  const onAlertClick = () => {
    setIsLetUsCallYouModalVisible(true);
    onLetUsCallYouFormSendSuccess();
  };
  const onLetUsCallYouFormSendSuccess = () => {
    addHotelLetUsCallYouSuccessEventToGTM({
      title: '',
      hotelName: data?.name || '',
      checkInDate: formatMomentDate(inDate, GTM_DATE_FORMAT),
      checkOutDate: formatMomentDate(outDate, GTM_DATE_FORMAT),
      serviceType: 'Hotel',
    });
  };

  const tesisFeature = data?.features?.find(
    (feature) => feature.name === 'Tesis Özellikleri'
  );

  const handleRemovePackageItems = async () => {
    setDisable(true);
    try {
      if (basketInfo?.isPackageBasket && basketInfo?.id) {
        unlockBasketAsync();
        await callService(
          {
            service: 'BasketsService',
            method: 'packagesRemove',
            version: 'v3',
          },
          {
            basketId: basketInfo.id,
            itemId: basketInfo.hotels[0].itemId ?? '',
          }
        );
      }
      toggleConfirmModal();
      prebookMutation.mutate();
    } catch (error) {
      setShowErrorModalDeletePackage(true);
    }
  };
  const beginDate = startDate
    ? new Date(formatArrayToString(startDate || ''))
    : '';
  const isVideo = data?.videos && data?.videos?.length > 0 ? true : false;

  return (
    <Container>
      <ScrollToTop />
      {data ? <HotelDetailSchemaOrg hotelDetail={data} /> : null}

      {currentCartProductsModal ? (
        <CurrentCartPackagesModal
          onClose={() => setCurrentCartProductsModal(false)}
          onConfirm={() => {
            handleConfirmRemoveCurrentCartPackage();
          }}
        />
      ) : null}
      {hotelDetailConceptData && data ? (
        <HotelDetailConcept
          roomsRef={roomsRef}
          onAlertClick={onAlertClick}
          isLetUsCallYouModalVisible={isLetUsCallYouModalVisible}
          hotelRoomList={hotelRoomList}
          onCloseCallMeModal={onCloseCallMeModal}
          onLetUsCallYouFormSendSuccess={onLetUsCallYouFormSendSuccess}
          inDate={inDate}
          outDate={outDate}
          hotelRooms={hotelRooms}
          room={room}
          roomCard={data}
          viewRoom={viewRoom}
          setHotelDateSelection={setHotelDateSelection}
          RoomsVariationsItems={RoomsVariationsItems}
          data={hotelDetailConceptData}
          title={data?.name || ''}
          installment={data?.maxInstallmentForWorld || ''}
          hotelName={data?.name || ''}
          breadcrumbItems={data?.breadcrumbs || []}
          city={data?.city || ''}
          country={data?.country || ''}
          longitude={data?.longitude}
          latitude={data?.latitude}
          hasAnnouncements={Boolean(
            data?.announcements && data?.announcements?.length
          )}
          toggleMap={toggleMap}
          onNavigate={handleNavigateRooms}
          onNavigateFeatures={handleNavigateFeatures}
          onToggleFeatures={toggleFeaturesModal}
          onToggleActiveFeatures={toggleActiveFeaturesModal}
          hotelData={data}
          ratingDetail={ratingDetail}
          isLocalHotel={data?.isLocalHotel || false}
        />
      ) : null}

      {data?.bankInstallments && showInstallments ? (
        <BankInstallments
          bankInstallments={data.bankInstallments}
          onClose={toggleInstallments}
        />
      ) : null}

      {loyaltyType ? (
        <LoyaltyVerifications
          action={handleLoyaltySubmit}
          loyaltyType={loyaltyType}
          onCancel={() => setModalLoyaltyType(null)}
        />
      ) : null}

      {showNormalHotel ? (
        <>
          {!isDesktop ? (
            <DetailSearchBox
              setSearchLoading={setSearchLoading}
              detail={true}
            />
          ) : null}

          {!isMobile ? (
            <BreadcrumbDesktop>
              <Breadcrumb
                setIsRedirecting={setSearchLoading}
                detail={true}
                items={data?.breadcrumbs || []}
              />
            </BreadcrumbDesktop>
          ) : null}

          <SearchBar
            ratingDetail={ratingDetail}
            setSearchLoading={setSearchLoading}
            data={data}
            onResearch={() => {
              setSelectedVariations([]);
            }}
          />
        </>
      ) : null}
      {showMap && data?.latitude && data?.longitude ? (
        <Modal
          borderRadius={isDesktop ? 8 : 0}
          width={isDesktop ? 960 : '100vw'}
          height={isDesktop ? '550px' : '98vh'}
          padding='0px 10px 10px 10px'
          onCancel={toggleMap}
          modalBodyHeight='100vh'
        >
          <Map latitude={data.latitude} longitude={data.longitude} />
        </Modal>
      ) : null}
      {isMobile && (isConceptError || data?.isExpedia) ? (
        <BreadcrumbWrapper>
          <Breadcrumb
            setIsRedirecting={setSearchLoading}
            items={data?.breadcrumbs || []}
          />
        </BreadcrumbWrapper>
      ) : null}

      {showHotelSkeleton ? (
        <HotelDetailSkeleton />
      ) : (
        <>
          {showNormalHotel ? (
            <Element name='overview' className='element'>
              <ImagesWrapper>
                <GalleryHeroImages
                  isSeturExecutive={data?.seturExecutive}
                  isSafe={data?.secure}
                  isSuggested={data?.suggests}
                  images={formatHotelImages(
                    data?.images?.slice(0, 3),
                    data?.name
                  )}
                  totalImages={totalImages}
                  viewGallery={toggleFeaturesModal}
                  videoViewGallery={toggleVideoFeaturesModal}
                  setActiveGalleryModalName={setActiveGalleryModalName}
                  isVideo={isVideo}
                  tourismCertificateText={data?.tourismCertificateText}
                  tourismCertificateNumber={data?.tourismCertificateNumber}
                />
              </ImagesWrapper>
            </Element>
          ) : null}
          <Layout>
            <Wrapper>
              {showNormalHotel ? (
                <Element name='overview' className='element'>
                  <SummaryCard
                    isBadge={tesisFeature?.features?.some(
                      (item) =>
                        item.name === 'Misafirler Öneriyor' && item.badge
                    )}
                    cityId={data?.cityId}
                    ratingDetail={ratingDetail}
                    badge={data?.features}
                    name={data?.name || ''}
                    description={data?.description || ''}
                    boardType={data?.boardType || ''}
                    city={data?.city || ''}
                    region={data?.region || ''}
                    country={data?.country || ''}
                    features={data?.mainFacilities}
                    prices={data?.lowestPrice}
                    isExpedia={data?.isExpedia}
                    isDomestic={data?.isDomestic}
                    userInfo={userInfo}
                    longitude={data?.longitude}
                    latitude={data?.latitude}
                    onNavigate={handleNavigateRooms}
                    onClickTripAdvisor={ClickTripAdvisor}
                    toggleMap={toggleMap}
                    onReadMore={handleNavigateAboutUs}
                    promotions={data?.promotions}
                    installments={data?.bankInstallments}
                    roomInformation={
                      getRoomInformations(
                        formatArrayToString(room),
                        formatArrayToString(startDate || []),
                        formatArrayToString(endDate || [])
                      ).message
                    }
                    bankInstallMentForWorld={data?.maxInstallmentForWorld}
                    status={
                      data?.rooms?.[0]?.details?.[0]?.informations?.[0]
                        ?.availabilityStatus
                    }
                    showKocAilem={showKocAilem}
                    showInstallments={toggleInstallments}
                    hotelId={data?.id}
                    imageUrl={data?.images?.[0]?.url ?? ''}
                    distanceList={data?.distanceList}
                    differenceItem={data}
                    countryCode={data?.countryCode}
                    basketHotelCount={basketInfo?.hotels?.length}
                  />
                </Element>
              ) : null}
              {data && data?.announcements?.length > 0 ? (
                <HotelBanners
                  handleNavigateAnnouncment={handleNavigateAnnouncment}
                  data={data?.banner}
                  scrollToBottom={scrollToBottom}
                  showNormalHotel={showNormalHotel ?? false}
                />
              ) : null}

              <Separator />
              {data?.events?.length ? (
                <HotelDetailEvents events={data.events} />
              ) : null}

              <Separator />
              {hotelDetailConceptData ? null : (
                <Element name='rooms' className='element'>
                  <div ref={roomsRef as MutableRefObject<HTMLDivElement>}>
                    {data?.nightCount && !data?.rooms?.length ? (
                      <RoomWarningInfo
                        onAlertClick={onAlertClick}
                        onCloseCallMeModal={onCloseCallMeModal}
                        onLetUsCallYouFormSendSuccess={
                          onLetUsCallYouFormSendSuccess
                        }
                        countryCode={data?.countryCode}
                        inDate={inDate}
                        outDate={outDate}
                        data={data}
                        hotelRoomList={hotelRoomList}
                        isLetUsCallYouModalVisible={isLetUsCallYouModalVisible}
                      />
                    ) : (
                      <RoomOptions
                        hotelId={data?.id ?? 0}
                        hotelName={data?.name || ''}
                        items={hotelRooms ?? []}
                        nightCount={data?.nightCount ?? 0}
                        roomNumber={isArray(room) ? room.length : 1}
                        viewRoom={viewRoom}
                        isExpedia={data?.isExpedia ?? false}
                        openDatePicker={setHotelDateSelection}
                        roomsVariationsItems={RoomsVariationsItems}
                        isLocalHotel={data?.isLocalHotel || false}
                        country={data?.countryCode}
                        basketHotelCount={basketInfo?.hotels?.length}
                      />
                    )}
                  </div>
                </Element>
              )}
              <Separator />
              <Element name='features' className='element'>
                {data?.seasonFeatures?.length || data?.generalConditions ? (
                  <div ref={featuresRef as MutableRefObject<HTMLDivElement>}>
                    <HotelFeatures
                      hotelFeatures={data.seasonFeatures}
                      beginDate={beginDate}
                      generalConditions={data.generalConditions}
                    />
                  </div>
                ) : null}
              </Element>

              <Separator />

              {data?.checkinBeginTime ? (
                <Element name='rules' className='element'>
                  <Rules
                    checkinBeginTime={data.checkinBeginTime}
                    checkinEndTime={data.checkinEndTime}
                    mandatoryFee={data.mandatoryFee}
                    optionalFee={data.optionalFee}
                    checkoutTime={data.checkoutTime}
                    checkinInstruction={data.checkinInstruction}
                    checkinSpecialInstruction={data.checkinSpecialInstruction}
                  />
                </Element>
              ) : null}

              <Separator />
              <Element name='about' className='element'>
                {data?.description ? (
                  <div ref={aboutsRef as MutableRefObject<HTMLDivElement>}>
                    <AboutUs description={data.description} />
                  </div>
                ) : null}
              </Element>

              <Separator />
              <Element name='announcements' className='element'>
                {data && data?.announcements?.length > 0 ? (
                  <div
                    ref={announcementsRef as MutableRefObject<HTMLDivElement>}
                  >
                    <Announcements data={data.announcements} />
                  </div>
                ) : null}
              </Element>
              <Element name='location' className='element'>
                {data?.latitude && data.longitude ? (
                  <HotelLocationWrapper>
                    <HotelLocation
                      locationName={data.name}
                      locationAddress={data.address}
                      latitude={data.latitude}
                      longitude={data.longitude}
                    />
                  </HotelLocationWrapper>
                ) : null}
              </Element>
              <Separator />

              <Element name='scoring' className='element'>
                <div ref={scoringRef as MutableRefObject<HTMLDivElement>}>
                  {ratingDetail?.rating &&
                  ratingDetail.provider === RatingProviderType.TRIP_ADVISOR ? (
                    <HotelDetailScoring
                      totalScoring={ratingDetail?.rating}
                      subratings={ratingDetail?.subratings}
                      reviews={ratingDetail?.reviews}
                      tripTypes={ratingDetail?.tripTypes}
                      reviewCount={ratingDetail?.totalReviewCount}
                    />
                  ) : null}
                  {ratingDetail?.rating &&
                  ratingDetail.provider === RatingProviderType.EXPEDIA ? (
                    <ExpediaHotelDetailScoring
                      totalScoring={ratingDetail?.rating}
                      subratings={ratingDetail?.subratings}
                      reviews={ratingDetail?.reviews}
                      tripTypes={ratingDetail?.tripTypes}
                      reviewCount={ratingDetail?.totalReviewCount}
                    />
                  ) : null}
                </div>
              </Element>

              <>
                {hotelRecommendations ? (
                  <IsInViewPort
                    onViewVisible={() => {
                      hotelYouLike(
                        hotelRecommendations?.data?.slice(0, 10),
                        startDate,
                        endDate,
                        false
                      );
                    }}
                  >
                    <HotelRecommendations
                      setIsRedirecting={setSearchLoading}
                      hotelRecommendations={hotelRecommendations}
                      startDate={formatArrayToString(startDate || '')}
                      endDate={formatArrayToString(endDate || '')}
                    />
                  </IsInViewPort>
                ) : null}
              </>
            </Wrapper>
          </Layout>
          {data?.metaContent ? (
            <SeoTextContainer>
              <SeoText text={data?.metaContent || ''} />
            </SeoTextContainer>
          ) : null}
          {selectedVariations.length ? (
            <PriceDetails
              isLoading={prebookMutation.isLoading}
              items={drawerItems()}
              onNavigate={handleNavigateTravelPlanning}
              onRemove={handleRemoveVariation}
              currency={data?.lowestPrice?.currencyCode}
              warningMessage={
                unSelectedRooms?.length
                  ? `Devam etmek için ${unSelectedRooms[0]}. odayı seçiniz`
                  : undefined
              }
            />
          ) : null}

          {modalViewInfo.galleriesDetailsModalShown ? (
            <DetailsAndGalleries
              {...detailsAndGalleriesProps}
              {...featuresProps}
              closeModal={() => {
                setGalleryDetailsModalViewInfo({
                  galleriesDetailsModalShown: false,
                  view: ModalView.NONE,
                  entityType: 'hotel' || 'room',
                });
                setStartImage(0);
                setViewedRoom(undefined);
              }}
              price={data?.lowestPrice}
              isOpen={modalViewInfo.galleriesDetailsModalShown}
              view={modalViewInfo.view}
              room={viewedRoom}
              announcements={data?.announcements}
              activeGalleryModalName={activeGalleryModalName}
              startImage={startImage}
              videos={data?.videos ?? []}
              control={control}
            />
          ) : null}
        </>
      )}
      {prebookMutation.isLoading ||
      searchLoading ||
      isHotelFetching ||
      isRemovePackageLoading ? (
        <GeneralLoader />
      ) : null}

      {prebookMutation.isError ? (
        <ErrorInfoModal
          title={errorModalStatusMessage.title}
          description={
            PAYMENT_STARTED === errorModalStatusMessage.description ? (
              <PaymentStartedErrorContainer>
                Ödeme başladığı için sepete ürün eklenemez, mevcut ödeme
                sayfasına gitmek için
                <LinkText href={`/odeme/${pid}`} prefetch={false}>
                  {' '}
                  buraya{' '}
                </LinkText>
                tıklayın
              </PaymentStartedErrorContainer>
            ) : errorModalStatusMessage.description.startsWith(
                'ACCEPTABLE_MIN_AGE_VIOLATION'
              ) ? (
              <>
                <Typography variant='caption1' color='D400'>
                  Bu otel için belirlenen yaş sınırı en az{' '}
                  <ItemBoldText>{ageValidation}</ItemBoldText> dir.
                </Typography>
              </>
            ) : (
              <BasketErrorText>
                {errorModalStatusMessage.description}
              </BasketErrorText>
            )
          }
          onCancel={errorInfoModalClosed}
        />
      ) : null}
      {confirmPackageModal ? (
        <ConfirmationModal
          disable={disable}
          onCancel={() => setConfirmModal(false)}
          onSubmit={handleRemovePackageItems}
          content='Sepetinizde bir Kıbrıs paket ürünü bulunmakta, devam etmeniz halinde sepetiniz silenecektir. Devam etmek ister misiniz?'
          title='Sepeti Sil'
        />
      ) : null}
      {showErrorModalDeletePackage ? (
        <ErrorModal
          onCancel={() => setShowErrorModalDeletePackage(false)}
          message='Ürün sepetten silinirken bir hata oluştu.'
        />
      ) : null}
    </Container>
  );
};

export default HotelDetail;
